/**
 * Network-first strategy implementation
 *
 * This strategy:
 * 1. Tries to get the resource from the network first
 * 2. If network fails, falls back to cache
 * 3. Updates the cache with the new network response if successful
 *
 * Best for: Resources that change frequently but should still work offline
 * when necessary (videos, dynamic content)
 */
import { debugLog } from "../utils/cache-helpers";
import { generateCacheKey } from "../utils/hash-generator";

/**
 * Network-first strategy for fetch requests
 * @param request The request to handle
 * @param cacheName The name of the cache to use
 * @param timeoutMs Timeout for network requests in milliseconds
 * @returns Promise resolving to the response
 */
export async function networkFirstStrategy(
  request: Request,
  cacheName: string,
  timeoutMs = 3000
): Promise<Response> {
  const cacheKey = generateCacheKey(request.url);

  try {
    // Try network first with timeout
    const networkPromise = fetchWithTimeout(request, timeoutMs);

    try {
      const networkResponse = await networkPromise;

      // Cache the successful response
      if (networkResponse.ok && networkResponse.status < 400) {
        const cache = await caches.open(cacheName);
        // Clone the response before caching it
        const responseToCache = networkResponse.clone();
        cache.put(cacheKey, responseToCache);
        debugLog(`Updated cache for ${request.url}`);
      }

      return networkResponse;
    } catch (error) {
      debugLog(
        `Network request failed for ${request.url}, falling back to cache`
      );

      // Network failed, try cache
      const cache = await caches.open(cacheName);
      const cachedResponse = await cache.match(cacheKey);

      if (cachedResponse) {
        debugLog(`Serving from cache for ${request.url}`);
        return cachedResponse;
      }

      // If both network and cache fail, throw the original error
      throw error;
    }
  } catch (error) {
    debugLog(`Error in network-first strategy for ${request.url}:`, error);
    throw error;
  }
}

/**
 * Network-first strategy for the Cache API
 * @param cache The cache object
 * @param url The URL to fetch
 * @param fetchOptions Options for the fetch request
 * @param timeoutMs Timeout for network requests in milliseconds
 * @returns Promise resolving to the response
 */
export async function networkFirstWithCacheAPI(
  cache: Cache,
  url: string,
  fetchOptions?: RequestInit,
  timeoutMs = 3000
): Promise<Response> {
  const cacheKey = generateCacheKey(url);

  try {
    // Try network first with timeout
    const networkPromise = fetchWithTimeout(url, timeoutMs, fetchOptions);

    try {
      const networkResponse = await networkPromise;

      // Cache the successful response
      if (networkResponse.ok && networkResponse.status < 400) {
        // Clone the response before caching it
        const responseToCache = networkResponse.clone();
        cache.put(cacheKey, responseToCache);
        debugLog(`Updated cache for ${url}`);
      }

      return networkResponse;
    } catch (error) {
      debugLog(`Network request failed for ${url}, falling back to cache`);

      // Network failed, try cache
      const cachedResponse = await cache.match(cacheKey);

      if (cachedResponse) {
        debugLog(`Serving from cache for ${url}`);
        return cachedResponse;
      }

      // If both network and cache fail, throw the original error
      throw error;
    }
  } catch (error) {
    debugLog(`Error in network-first strategy for ${url}:`, error);
    throw error;
  }
}

/**
 * Fetch with timeout
 * @param request The request or URL to fetch
 * @param timeoutMs Timeout in milliseconds
 * @param options Fetch options
 * @returns Promise resolving to the response
 */
async function fetchWithTimeout(
  request: Request | string,
  timeoutMs: number,
  options?: RequestInit
): Promise<Response> {
  const controller = new AbortController();
  const { signal } = controller;

  // Create a timeout that aborts the fetch
  const timeout = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    // Merge the abort signal with any existing options
    const fetchOptions = {
      ...(options || {}),
      signal,
    };

    const response = await fetch(request, fetchOptions);
    clearTimeout(timeout);
    return response;
  } catch (error) {
    clearTimeout(timeout);
    throw error;
  }
}
